import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { toastSuccess, toastError, errorMessage } from '../../lib/toast';
import { CollaboratorPicker } from '../buildApp/AppRequestForm';

/**
 * Self-contained form for proposing a new tool.
 *
 * Props:
 *   initial   — prefill data for edit mode {name, description, link, icon_url}
 *   editId    — number|null; when set renders edit mode (no approver/watcher pickers)
 *   onSaved   — callback after a successful save
 *   onCancel  — callback when the user cancels
 *   firstFieldRef — optional ref the host drawer uses to focus the first field on open
 */
export default function ProposeToolForm({ initial = null, editId = null, onSaved, onCancel, onBusyChange, firstFieldRef }) {
  const { user } = useAuth();
  const isEdit = editId != null;

  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [link, setLink] = useState(initial?.link || '');
  const [iconUrl, setIconUrl] = useState(initial?.icon_url || '');
  const [approvers, setApprovers] = useState([]);
  const [watchers, setWatchers] = useState([]);
  const [formError, setFormError] = useState('');
  // Field-level: `required` lets a whitespace-only description through.
  const [descError, setDescError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const addApprover = (u) => setApprovers((prev) => (prev.some((a) => a.id === u.id) ? prev : [...prev, u]));
  const removeApprover = (id) => setApprovers((prev) => prev.filter((a) => a.id !== id));

  const addWatcher = (u) => setWatchers((prev) => (prev.some((w) => w.id === u.id) ? prev : [...prev, u]));
  const removeWatcher = (id) => setWatchers((prev) => prev.filter((w) => w.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Name and link are `required` and gate the submit button. A description
    // of only spaces passes `required`, so it gets its own inline message.
    if (!name.trim() || !link.trim()) return;
    if (!description.trim()) {
      setDescError('A description is required.');
      return;
    }

    if (!isEdit && approvers.length === 0) {
      setFormError('Pick at least one approver.');
      return;
    }
    setFormError('');
    setSubmitting(true);
    if (onBusyChange) onBusyChange(true);

    try {
      if (isEdit) {
        await axios.put(
          `${API_BASE_URL}/api/tool_requests/${editId}`,
          {
            name: name.trim(),
            description: description.trim(),
            link: link.trim(),
            icon_url: iconUrl.trim() || null,
          },
          { withCredentials: true }
        );
        toastSuccess('Proposal updated.');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/tool_requests`,
          {
            name: name.trim(),
            description: description.trim(),
            link: link.trim(),
            icon_url: iconUrl.trim() || null,
            approver_ids: approvers.map((a) => a.id),
            watcher_ids: watchers.map((w) => w.id),
          },
          { withCredentials: true }
        );
        toastSuccess('Proposal sent for approval.');
      }
      if (mountedRef.current) {
        setSubmitting(false);
        if (onBusyChange) onBusyChange(false);
        onSaved();
      }
    } catch (err) {
      if (mountedRef.current) {
        toastError(errorMessage(err, 'Could not submit proposal'));
        setSubmitting(false);
        if (onBusyChange) onBusyChange(false);
      }
    }
  };

  const approverExcludeIds = user ? [user.id] : [];
  const watcherExcludeIds = user ? [user.id, ...approvers.map((a) => a.id)] : approvers.map((a) => a.id);

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="pt-name">Tool name <span className="rq-required" aria-hidden="true">*</span></label>
        <input
          id="pt-name"
          ref={firstFieldRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Notion"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="pt-description">Description <span className="rq-required" aria-hidden="true">*</span></label>
        <textarea
          id="pt-description"
          value={description}
          onChange={(e) => { setDescription(e.target.value); if (descError) setDescError(''); }}
          aria-invalid={descError ? 'true' : undefined}
          aria-describedby={descError ? 'pt-description-error' : undefined}
          placeholder="What is it for?"
          rows={2}
          required
          style={{ resize: 'vertical' }}
        />
        {descError && (
          <p id="pt-description-error" className="rq-note" role="alert" style={{ margin: '4px 0 0' }}>
            {descError}
          </p>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="pt-link">Link <span className="rq-required" aria-hidden="true">*</span></label>
        <input
          id="pt-link"
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://…"
          required
          data-testid="tool-request-link"
        />
      </div>

      <div className="form-group">
        <label htmlFor="pt-icon">Icon URL (optional)</label>
        <input
          id="pt-icon"
          type="url"
          value={iconUrl}
          onChange={(e) => setIconUrl(e.target.value)}
          placeholder="https://… (icon image)"
        />
      </div>

      {!isEdit && (
        <>
          <div className="form-group" style={{ marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 600 }}>Approvers *</h4>
            <CollaboratorPicker
              collaborators={approvers}
              onAdd={addApprover}
              onRemove={removeApprover}
              searchUrl="/api/tool-proposal/approvers-search"
              excludeIds={approverExcludeIds}
              hint="Pick the manager(s) who approve this tool."
              placeholder="Search managers by name or email…"
              noMatchLabel="No managers match"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 600 }}>Watchers</h4>
            <CollaboratorPicker
              collaborators={watchers}
              onAdd={addWatcher}
              onRemove={removeWatcher}
              searchUrl="/api/tool-proposal/directory"
              excludeIds={watcherExcludeIds}
              hint="Optionally add people to keep in the loop."
              placeholder="Search teammates by name or email…"
            />
          </div>
        </>
      )}

      {formError && (
        <p className="form-error rq-note" role="alert" style={{ margin: '0 0 12px' }}>
          {formError}
        </p>
      )}

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || !name.trim() || !link.trim() || !description}
        >
          {submitting
            ? (isEdit ? 'Saving…' : 'Submitting…')
            : (isEdit ? 'Save changes' : 'Submit request')}
        </button>
      </div>
    </form>
  );
}
